/**
 * Shared HTTP client for `/api/v1/*` in the web app.
 * Prefer `apiGet` / `apiPost` / `apiPatch` / `apiDelete` from domain `*Api` modules under `hooks/`.
 */

import { isSignOutApiBlocked } from "@sukha/query/signOutGate";
import { resolveServerApiBaseUrl } from "@/lib/api/baseUrl";

export function getApiBaseUrl(): string {
  const isServer = typeof window === "undefined";
  return isServer
    ? resolveServerApiBaseUrl()
    : "";
}

export interface ApiErrorBody {
  message?: string;
  code?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly body: ApiErrorBody | null;

  constructor(
    message: string,
    options: { status: number; code?: string; body?: ApiErrorBody | null },
  ) {
    super(message);
    this.name = "ApiError";
    this.status = options.status;
    this.code = options.code;
    this.body = options.body ?? null;
  }
}

export interface ApiClientOptions {
  /** Clerk or custom bearer token for server-side calls */
  token?: string;
  signal?: AbortSignal;
  /** Merge into init.headers */
  headers?: HeadersInit;
}

function buildHeaders(
  token: string | undefined,
  extra?: HeadersInit,
): HeadersInit {
  const h = new Headers(extra ?? {});
  if (!h.has("Content-Type")) {
    h.set("Content-Type", "application/json");
  }
  if (token) {
    h.set("Authorization", `Bearer ${token}`);
  }
  return h;
}

async function parseJsonBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/**
 * Low-level request helper. Prefer `apiGet` / `apiPost` / `apiPatch` / `apiDelete`.
 */
export async function apiRequest<T>(
  path: string,
  init: RequestInit = {},
  options: ApiClientOptions = {},
): Promise<T> {
  if (isSignOutApiBlocked()) {
    throw new DOMException("Sign-out in progress", "AbortError");
  }

  const baseUrl = getApiBaseUrl();
  const url = path.startsWith("http") ? path : `${baseUrl}${path}`;

  const mergedInit: RequestInit = {
    ...init,
    signal: options.signal ?? init.signal,
    headers: buildHeaders(options.token, init.headers as HeadersInit | undefined),
  };

  const response = await fetch(url, mergedInit);

  if (!response.ok) {
    const parsed = await parseJsonBody(response);
    const body =
      parsed && typeof parsed === "object"
        ? (parsed as ApiErrorBody)
        : null;
    const messageFromJson =
      body && typeof body.message === "string" ? body.message : undefined;
    const message =
      messageFromJson ||
      `Request failed: ${response.status} ${response.statusText}`;
    const code =
      body && typeof body.code === "string" ? body.code : undefined;
    throw new ApiError(message, {
      status: response.status,
      code,
      body,
    });
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type");
  if (contentType?.includes("application/json")) {
    return JSON.parse(text) as T;
  }

  return text as unknown as T;
}

export function apiGet<T>(
  path: string,
  options: ApiClientOptions = {},
): Promise<T> {
  return apiRequest<T>(path, { method: "GET" }, options);
}

export function apiPost<T>(
  path: string,
  body: unknown,
  options: ApiClientOptions = {},
): Promise<T> {
  return apiRequest<T>(
    path,
    {
      method: "POST",
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    options,
  );
}

export function apiPatch<T>(
  path: string,
  body: unknown,
  options: ApiClientOptions = {},
): Promise<T> {
  return apiRequest<T>(
    path,
    {
      method: "PATCH",
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    options,
  );
}

export function apiPut<T>(
  path: string,
  body: unknown,
  options: ApiClientOptions = {},
): Promise<T> {
  return apiRequest<T>(
    path,
    {
      method: "PUT",
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    options,
  );
}

export function apiDelete<T>(
  path: string,
  options: ApiClientOptions = {},
): Promise<T> {
  return apiRequest<T>(path, { method: "DELETE" }, options);
}
